import { BrandService } from '@common/services/brand/brand.service';
import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentReference, Query, QueryFn } from '@angular/fire/firestore';
import { Nationality } from '@common/models/places.model';
import { from, merge, Observable, of, throwError, timer, zip, combineLatest } from 'rxjs';
import { catchError, delay, filter, map, mergeAll, mergeMap, retryWhen, scan, skipWhile, switchMap, take, tap, toArray } from 'rxjs/operators';
import { Consumer } from '@common/models/consumer.model';
import { ConsumerSearchCentersFilter } from '@common/enums/consumer-search-centers-filter.enum';
import { UserProviderService } from '../providers/user-provider.service';
import { Brand } from '@common/interfaces/brand.interface';
import { Stand } from '@common/interfaces/stand.interface';
import { Center } from '@common/interfaces/center.interface';
import { Roles } from '@common/enums/roles.enum';
import { AngularFireStorage } from '@angular/fire/storage';
import { ConsumerSignature } from '@common/models/consumer-signature.model';
import { firestore } from 'firebase/app';
import { ConsumerBrand } from '@common/models/consumer-brand.model';
import { ConsumerStand } from '@common/models/consumer-stand.model';
import formatDate from 'date-fns/format';
import * as _ from 'lodash';
import { UserModel } from '@common/models/user/user.model';
import { firestoreMaxClauses } from '@common/const/firebase-max-clauses.const';
import { PermissionService } from '../permissions/permission.service';
import { NgxRolesObject } from 'ngx-permissions';
import { PermAction, PermActionOption, Permission } from '@common/enums/permission.enum';
import { TmpConsumerId } from '@common/models/tmp_consumer_id.model';
import { ConnectionStatusService } from '../connection-status/connection-status.service';
import { ConsumerGetMember } from '@common/models/consumer-get-member.model';
import { ConsumerPreferences } from '@common/models/consumer-preferences.mode';
import { useDivision } from '@common/utils/useDivision';
import { Divisions } from '@common/const/config.const';

@Injectable({
  providedIn: 'root'
})
export class ConsumerService {
  private readonly collectionName = 'CONSUMIDORAS';
  private readonly nationalitiesCollectionName = 'NACIONALIDADES';
  private readonly consumerBrandCollectionName = 'CONSUMIDORA_MARCA';
  private readonly consumerPreferencesCollectionName = 'CONSUMIDORA_PREFERENCIAS';
  private readonly consumerSignatureCollectionName = 'CONSUMIDORAS_FIRMA';
  private readonly consumerStandCollectionName = 'CONSUMIDORA_STAND';
  private readonly consumerGetMemberCollectionName = 'CONSUMIDORA_GET_MEMBER';
  private readonly consumerGetMemberSubcollectionName = 'REFERIDOS';
  private readonly sequencesCollectionName = 'SECUENCIAS';
  private readonly consumerHistoricCollectionName = 'HISTORICO_CONSUMIDORAS';
  private readonly logmailCollectionName = 'LOG_MAILS';
  private readonly signatureBasePath = 'firmas';
  private readonly tmpConsumerIdCollectionName = 'TMP_CONSUMIDORA_ID';
  userPermissions: NgxRolesObject;

  constructor(
    private afFirestore: AngularFirestore,
    private afStorage: AngularFireStorage,
    private userService: UserProviderService,
    private brandService: BrandService,
    private permissionService: PermissionService,
    private connectionStatusService: ConnectionStatusService,
    private af: AngularFirestore,
  ) { }

  public static consumerName(consumer: Consumer): string {
    if (!consumer) {
      return '';
    }

    return `${consumer.NOMBRE || ''} ${consumer.APELLIDO1 || ''} ${consumer.APELLIDO2 || ''}`;
  }

  public static consumerContact(consumer: Consumer): string {
    if (!consumer) {
      return '';
    }

    const phone = consumer.MOVIL || consumer.TELEFONO || '';
    const email = consumer.EMAIL || '';
    return phone.concat(phone && email ? ' | ' : '', email);
  }

  public static consumerGender(consumer: Consumer): string {
    return consumer?.SEXO ? 'Consumers.ConsumerConfirm.PersonalInformation.' + consumer.SEXO : '';
  }

  public static consumerBirthDate(consumer: Consumer): Date {
    return consumer?.ANOFN && consumer?.MESFN && consumer.DIAFN
      ? new Date(`${consumer.ANOFN}-${consumer.MESFN}-${consumer.DIAFN}`)
      : null;
  }

  public static consumerAddress(consumer: Consumer): string {
    return ''.concat(
      consumer.NOMBREVIA && consumer.TIPOVIA ? consumer.TIPOVIA + ' ' : '',
      consumer.NOMBREVIA ? consumer.NOMBREVIA + ' ' : '',
      consumer.NOMBREVIA && consumer.NUMEROVIA ? consumer.NUMEROVIA : '',
      consumer.NOMBREVIA ? ', ' : '',
      consumer.LOCALIDAD ? consumer.LOCALIDAD : '',
    );
  }

  public getConsumers(
    options: {
      filters?: { textSearch: string, searchIn: ConsumerSearchCentersFilter };
      offset?: number;
      limit?: number;
      brandId: number;
    }
  ): Observable<Consumer[]> {
    return this.userParams$(
      Number(this.userService.user.role.id),
      options.filters?.searchIn === ConsumerSearchCentersFilter.STAND
    ).pipe(
      take(1),
      switchMap(({ stand, brands }) =>
        zip(
          ...new Array(
            Math.ceil(
              (brands && options.filters?.searchIn === ConsumerSearchCentersFilter.ALL ? brands.length : 1) / firestoreMaxClauses
            )
          ).fill(undefined).map(
            (v, brandIndex) =>
              this.searchByField(options?.filters?.textSearch).map(
                field =>
                  this.afFirestore
                    .collection<Consumer>(
                      this.collectionName,
                      ref => this.getSearchQuery(ref, field, options, { stand, brands }, brandIndex)
                    )
                    .get()
                    .pipe(
                      map(snapshot => snapshot.docs.map(d => d.data()))
                    )
              )
          ).flat()
        ).pipe(
          // Flat array and filter unique results
          map(consumers => {
            const hash: { [key: number]: boolean } = {};
            return consumers
              .flat()
              .filter(consumer => hash[consumer.ID_CONSUMIDORA] ? false : hash[consumer.ID_CONSUMIDORA] = true)
              .slice(0, options.limit)
              .map(consumer => ({
                ...consumer,
                MI_STAND: consumer.array_CONSUMIDORA_STAND?.includes(stand.ID_STAND.toString()),
                FECHA_ALTA_C: (consumer.FECHA_ALTA_C as unknown as firestore.Timestamp)?.toDate(),
                FECHA_BAJA_C: (consumer.FECHA_BAJA_C as unknown as firestore.Timestamp)?.toDate(),
              }));
          }),
          switchMap(consumers => consumers
            .map(consumer =>
              this.af.collection<ConsumerBrand>('CONSUMIDORA_MARCA',
                ref =>
                  ref.where('ID_CONSUMIDORA', '==', consumer.ID_CONSUMIDORA)
                    .where('FECHA_BAJA_CM', '!=', null)
                    .where('ID_MARCA', '==', options.brandId)
              )
                .get()
                .pipe(
                  map((data) => {
                    const brands = data.docs.map(d => d.data())
                    return brands?.length > 0 ? null : consumer
                  })
                )
            )),
          mergeAll(),
          toArray(),
          map(data => data.flat()),
          map((data) => data.filter(d => d !== null)),
        )
      )
    );
  }

  public getConsumerById(id: string): Observable<Consumer> {
    return this.afFirestore
      .collection<Consumer>(this.collectionName)
      .doc<Consumer>(id)
      .valueChanges()
      .pipe(
        map(consumer =>
          consumer
            ? {
              ...consumer,
              FECHA_ALTA_C: ((consumer.FECHA_ALTA_C as unknown) as firestore.Timestamp)?.toDate(),
              FECHA_BAJA_C: ((consumer.FECHA_BAJA_C as unknown) as firestore.Timestamp)?.toDate()
            }
            : undefined
        )
      );
  }  getConsumersBrandsDetails(
    email: string,
    phone: string,
    division: string
  ): Observable<Array<{ id: number; nombre: string; apellido: string; movil: string; brands: string[]; email: string }>> {
    // Increase batch size to ensure we capture all potential duplicates
    const BATCH_SIZE = 50;

    // Prepare the base query without limit initially to ensure we get all potential matches
    const baseQuery = (ref: any) => {
      return ref
        .where('FECHA_BAJA_C', '==', null)
        .where('DUPLICADO_CS', '==', null);
    };

    // Create separate queries for email and phone, then combine results
    const queries: Observable<Consumer[]>[] = [];

    // Enhanced logic: search for ALL potential duplicates
    // 1. If email provided, find consumers with that exact email
    if (email && email.trim()) {
      const emailQuery = this.afFirestore.collection<Consumer>('CONSUMIDORAS', ref => {
        return baseQuery(ref)
          .where('EMAIL', '==', email.trim())
          .limit(BATCH_SIZE);
      });
      queries.push(
        from(emailQuery.get()).pipe(
          map(snapshot => snapshot.docs.map(doc => ({ ...doc.data(), docId: doc.id })))
        )
      );
    }

    // 2. If phone provided, find consumers with that exact phone
    if (phone && phone.trim()) {
      const phoneQuery = this.afFirestore.collection<Consumer>('CONSUMIDORAS', ref => {
        return baseQuery(ref)
          .where('MOVIL', '==', phone.trim())
          .limit(BATCH_SIZE);
      });
      queries.push(
        from(phoneQuery.get()).pipe(
          map(snapshot => snapshot.docs.map(doc => ({ ...doc.data(), docId: doc.id })))
        )
      );
    }

    // If no email or phone provided, return empty
    if (queries.length === 0) {
      return of([]);
    }

    // Execute all queries and combine results, ensuring we capture ALL potential duplicates
    const combinedQuery = queries.length === 1 
      ? queries[0] 
      : combineLatest(queries).pipe(
          map(results => {
            // Flatten all results and remove duplicates by ID_CONSUMIDORA
            // This ensures that if the same consumer appears in multiple queries, 
            // we only include them once
            const allConsumers = results.flat();
            const uniqueConsumers = new Map();
            allConsumers.forEach(consumer => {
              // Use ID_CONSUMIDORA as the key to avoid duplicates
              if (!uniqueConsumers.has(consumer.ID_CONSUMIDORA)) {
                uniqueConsumers.set(consumer.ID_CONSUMIDORA, consumer);
              }
            });
            return Array.from(uniqueConsumers.values());
          })
        );    return combinedQuery.pipe(
      // Early return if no consumers found
      switchMap(consumers => {
        console.log('🔍 Potential duplicate consumers found:', consumers.length);
        console.log('📋 Consumer details:', consumers.map(c => ({
          id: c.ID_CONSUMIDORA,
          email: c.EMAIL,
          movil: c.MOVIL,
          nombre: c.NOMBRE
        })));
        
        if (consumers.length === 0) return of([]);

        // Get all consumer IDs
        const consumerIds = consumers.map(c => c.ID_CONSUMIDORA);

        // Batch fetch all consumer brands in one query
        return from(
          this.afFirestore
            .collection<ConsumerBrand>('CONSUMIDORA_MARCA', ref =>
              ref
                .where('ID_CONSUMIDORA', 'in', consumerIds)
                .where('FECHA_BAJA_CM', '==', null)
            )
            .get()
        ).pipe(
          map(brandsSnapshot => {
            console.log('🏷️ Found consumer brands:', brandsSnapshot.docs.length);
            
            // Create a map of consumer ID to their brands
            const consumerBrandsMap = new Map<number, number[]>();

            brandsSnapshot.docs.forEach(doc => {
              const data = doc.data();
              const consumerId = typeof data.ID_CONSUMIDORA === 'string'
              ? parseInt(data.ID_CONSUMIDORA, 10)
              : data.ID_CONSUMIDORA;

              if (!consumerBrandsMap.has(consumerId)) {
                consumerBrandsMap.set(consumerId, []);
              }
              const brands = consumerBrandsMap.get(consumerId);
              if (brands) {
                brands.push(data.ID_MARCA);
              }
            });
            
            console.log('🗺️ Consumer brands map:', Object.fromEntries(consumerBrandsMap));
            return { consumers, consumerBrandsMap };
          })
        );
      }),

      // Fetch all unique brands in one query
      switchMap((data: any[] | { consumers: any[]; consumerBrandsMap: Map<number, number[]> }) => {
        if (Array.isArray(data)) {
          return of([]);
        }

        const allBrandIds = Array.from(data.consumerBrandsMap.values()).flat();
        if (allBrandIds.length === 0) {
          console.log('⚠️ No brands found for any consumer, returning empty result');
          return of([]);
        }

        // Get unique brand IDs
        const uniqueBrandIds = [...new Set(allBrandIds)];
        console.log('🎯 Unique brand IDs to fetch:', uniqueBrandIds);

        // Batch fetch all brands
        return from(
          this.afFirestore
            .collection<Brand>('MARCAS', ref =>
              ref.where('ID_DIVISION', '==', division)
            )
            .get()
        ).pipe(
          map(brandsSnapshot => {
            // Create a map of brand ID to brand name
            const brandNameMap = new Map<number, string>();
            brandsSnapshot.docs.forEach(doc => {
              const data = doc.data();
              brandNameMap.set(Number(doc.id), data.NOMBRE_MARCA);
            });

            // Build final result
            const finalResult = data.consumers.map(consumer => ({
              id: consumer.ID_CONSUMIDORA,
              nombre: consumer.NOMBRE,
              apellido: `${consumer.APELLIDO1} ${consumer.APELLIDO2}`,
              movil: consumer.MOVIL,
              email: consumer.EMAIL,
              brands: (data.consumerBrandsMap.get(consumer.ID_CONSUMIDORA) || [])
                .map(brandId => brandNameMap.get(brandId))
                .filter((brand): brand is string => brand !== undefined)
            }));
            
            console.log('✅ Final duplicate detection result:', finalResult);
            return finalResult;
          })
        );
      })
    );
  }


  public getConsumerPreferencesByDivision(): Observable<ConsumerPreferences[]> {
    const userDiv = JSON.parse(localStorage.getItem('userDiv'));
    const ID_DIVISION = userDiv.roleData.ID_DIVISION;
    return this.afFirestore.collection<ConsumerPreferences>(this.consumerPreferencesCollectionName)
      .valueChanges()
      .pipe(
        map(consumerPreferences => consumerPreferences.filter(cp => cp.DIVISIONS.includes(ID_DIVISION)))
      );
  }

  public getConsumerPreferencesByDivisionControls(): Observable<string[]> {
    return this.getConsumerPreferencesByDivision()
      .pipe(
        map(consumerPreferences => {
          const labels = consumerPreferences.map(cp => cp?.VALUE);
          if (labels.length === 0) throw new Error(`No labels found. Check firestore collection ${this.consumerPreferencesCollectionName}`);
          return labels;
        }),
        catchError(error => throwError(error.message))
      );
  }

  /**
   * Given an ID it will look for the document on CONSUMIDORAS and TMP_CONSUMIDORA_ID (and then) CONSUMIDORAS. Then it will return the first document that arrives.
   *
   * @param {string} id
   * @return {*}  {Observable<Consumer>}
   * @memberof ConsumerService
   */
  public getConsumerByIdOrTmpId(id: string): Observable<Consumer> {
    const numberOfRetries: number = 10;
    const queryIdConsumidora: QueryFn = (ref) => ref.where('ID_CONSUMIDORA', '==', Number(id))
    const consumerByNumericIdCall$: Observable<Consumer> = this.afFirestore.collection<Consumer>(this.collectionName, queryIdConsumidora).get().pipe(
      switchMap((snapshot) => {
        if (snapshot.docs.length > 0) {
          return of(snapshot.docs[0].data())
        }
        throw new Error();
      }),
      retryWhen(error =>
        error.pipe(
          scan((acc, error) => {
            if (acc > numberOfRetries) throw error;
            return acc + 1;
          }, 1),
          delay(1000),
        )
      ),
      catchError((err, caught) => {
        return of(null);
      }),
      skipWhile((value) => value === null),
    )

    return consumerByNumericIdCall$;
  }

  public getConsumerRefById(id: number | string): DocumentReference<Consumer> {
    return this.afFirestore.doc<Consumer>(`${this.collectionName}/${id}`).ref;
  }

  /**
   * @description
   * Returns a list of nationalities.
   *
   */
  public getConsumerNationalities(): Observable<Nationality[]> {
    return this.afFirestore
      .collection<Nationality>(this.nationalitiesCollectionName)
      .valueChanges();
  }

  public getConsumerPreferences(brandId: number, consumerId: number) {
    const query: QueryFn = ref =>
      ref
        .where('ID_CONSUMIDORA', 'in', [consumerId.toString(), consumerId])
        .where('ID_MARCA', '==', brandId);
    return this.afFirestore
      .collection<ConsumerBrand>(this.consumerBrandCollectionName, query)
      .valueChanges()
      .pipe(
        map(({ 0: doc }) => {
          let prefs = {
            LOPD_CARTAS: doc?.LOPD_CARTAS ?? false,
            LOPD_LLAMADAS: doc?.LOPD_LLAMADAS ?? false,
            LOPD_EMAIL: doc?.LOPD_EMAIL ?? false,
            LOPD_SMS: doc?.LOPD_SMS ?? false,
            PROGRAMA_FIDELIDAD: doc?.PROGRAMA_FIDELIDAD ?? 0,
            PERMITE_LLAMADAS_MOVIL: doc?.PERMITE_LLAMADAS_MOVIL ?? false,
            PERMITE_LLAMADAS_TELEFONO: doc?.PERMITE_LLAMADAS_TELEFONO ?? false
          } as ConsumerBrand;
          if (useDivision() == Divisions.GC) {
            prefs = {
              ...prefs,
              MAQUILLAJE: doc?.MAQUILLAJE ?? false,
              FACIAL_BEAUTY: doc?.FACIAL_BEAUTY ?? false,
              OTROS: doc?.OTROS ?? false
            }
          } else {
            prefs = {
              ...prefs,
              TRATAMIENTO: doc?.TRATAMIENTO ?? false,
              MAQUILLAJE: doc?.MAQUILLAJE ?? false,
              PERFUME: doc?.PERFUME ?? false,
              HOMBRE: doc?.HOMBRE ?? false,
            }
          }
        return prefs;
        })
      );
  }

  public getConsumerFechaAltaCM(brandId: number, consumerId: number) {
    const query: QueryFn = ref =>
      ref
        .where('ID_CONSUMIDORA', 'in', [consumerId.toString(), consumerId])
        .where('ID_MARCA', '==', brandId);
    return this.afFirestore
      .collection<ConsumerBrand>(this.consumerBrandCollectionName, query)
      .valueChanges()
      .pipe(
        map(({ 0: doc }) => {
          return {
            FECHA_ALTA_CM: doc?.FECHA_ALTA_CM ?? new Date(),
          };
        })
      );
  }

  public saveSignature(id: string, signatureBase64String: string) {
    const ref = this.afStorage.ref(`${this.signatureBasePath}/${id}.jpg`);
    return from(ref.putString(signatureBase64String, 'base64', { contentType: 'image/jpeg' })
      .then(async () => this.afFirestore.doc(`${this.consumerSignatureCollectionName}/${id}`)
        .set({ HayFirmaCapturada: true, ID_CONSUMIDORA: id }, { merge: true })
      )
      // borrar el campo firma del documento .then(//)
    );
  }

  public getSignatureDate(id: number | string) {
    return this.afFirestore.doc<ConsumerSignature>(`${this.consumerSignatureCollectionName}/${id}`).valueChanges();
  }

  public getRevisarLOPD(consumerId: string): Observable<boolean> {
    return this.afFirestore
      .collection<Consumer>(this.collectionName)
      .doc<Consumer>(consumerId)
      .valueChanges()
      .pipe(
        take(1),
        map(data => data.REVISAR_LOPD)
      )
  }

  public async newOrSaveConsumer(
    isANewConsumer: boolean,
    formData: any,
    consumerTempId: number,
    currentUser: UserModel,
    currentUserBrandStandDefault: [Brand, Stand, Center],
    communicationsChannelsSettings: {
      [key: string]: {
        LOPD_CARTAS: boolean;
        LOPD_LLAMADAS: boolean;
        LOPD_EMAIL: boolean;
        LOPD_SMS: boolean;
      }
    },
    digitalSignatureId?: string
  ) {
    let batch: firestore.WriteBatch = this.afFirestore.firestore.batch();
    if (_.isNil(consumerTempId)) {
      throw new Error('No consumer ID provided, not even a temporary one.');
    }

    // LOGMAIL
    if (!_.isNil(formData.EMAIL)) {
      batch = this.newLogMail(consumerTempId, formData.EMAIL, batch);
    } else {
      throw new Error('Cannot logmail without an e-mail');
    }

    const [ANOFN, MESFN, DIAFN] = formData.FECHA_NACIMIENTO
      ? formData.FECHA_NACIMIENTO.split('-')
      : [null, null, null];

    const activeConsumerStand = [currentUserBrandStandDefault[1].ID_STAND];
    const activeBrandId = [currentUserBrandStandDefault[0].ID_MARCA];    try {
      batch = await this.saveConsumer(formData, consumerTempId, DIAFN, MESFN, ANOFN, activeConsumerStand, batch, currentUser.id, isANewConsumer);
    } catch (error) {
      throw new Error(`Error saving the consumer - ${error}`);
    }

    // CONSUMIDORA_MARCA
    if (!_.isNil(formData.BRANDS)) {
      const brands = this.selectedBrands(formData.BRANDS);
      batch = await this.deleteCustomerFromUncheckedBrands(
        this.selectedBrands(formData.BRANDS, true),
        consumerTempId,
        batch
      );
      batch = await this.removeConsumerFromUncheckedBrandsStands(
        this.selectedBrands(formData.BRANDS, true),
        consumerTempId,
        activeConsumerStand,
        batch
      )
      batch = await this.addConsumerFromCheckedBrandsStands(this.selectedBrands(formData.BRANDS), consumerTempId, activeConsumerStand, currentUser.id, batch)
      batch = await this.saveConsumerBrandPreferences(consumerTempId, currentUser,
        formData, brands, isANewConsumer, batch, communicationsChannelsSettings, digitalSignatureId);
      // HISTORICO_CONSUMIDORAS
      batch = await this.saveHistoricConsumerBrand(
        currentUser,
        consumerTempId,
        formData,
        DIAFN,
        MESFN,
        ANOFN,
        brands,
        batch
      );

      try {
        return batch.commit();
      } catch (error) {
        batch = undefined;
        throw new Error(`Error saving data related with brands - ${error}`);
      }
    }

  }

  /**
   * This method is painfully similar to ConsumerSaleService.addConsumerStand(). That's because both methods do the same thing, but it differs in the context.
   * This needs to be on its own service and adapt both services to work with the new one.
   *
   * @param {string[]} brandsArr
   * @param {string} consumerTempId
   * @param {number[]} standsArr
   * @param {string} idUser
   * @return {*}
   * @memberof ConsumerService
   */
  async addConsumerFromCheckedBrandsStands(brandsArr: string[], consumerTempId: number, standsArr: number[], idUser: string, batch: firestore.WriteBatch) {
    // CONSUMIDORA_STAND
    const operations = brandsArr.map(brandId => {
      return standsArr.map(standId => {
        const consumerStandDoc = this.afFirestore.doc<ConsumerStand>(`CONSUMIDORA_STAND/${consumerTempId}-${brandId}-${standId}`);
        return consumerStandDoc.get().pipe(
          map(doc => doc.exists && !doc.data().FECHA_BAJA_CS),
          switchMap(exists => {
            if (exists) {
              return of(undefined);
            }

            return of(batch.set(consumerStandDoc.ref, {
              COMENTARIO_CONSUMIDORA: '',
              ESTADO: 1,
              FECHA_ALTA_CS: new Date(),
              FECHA_BAJA_CS: null,
              ID_CONSUMIDORA: consumerTempId,
              ID_MARCA: parseInt(brandId, 10),
              ID_STAND: standId,
              ID_USUARIO: parseInt(idUser, 10),
              ID_USUARIO_ALTA: parseInt(idUser, 10),
              VIP_MANUAL: ''
            }));

          })
        ).toPromise();
      })
    }).flat()
    return Promise.all(operations).then(() => batch);
  }

  removeConsumerFromUncheckedBrandsStands(brandsArr: string[], consumerTempId: number, standsArr: number[], batch: firestore.WriteBatch) {
    // CONSUMIDORA_STAND
    const operations = brandsArr.map(brandId => {
      return standsArr.map(standId => {
        const firestoreDoc = this.afFirestore.doc<Partial<ConsumerStand>>(`${this.consumerStandCollectionName}/${consumerTempId}-${brandId}-${standId}`);
        return firestoreDoc
          .get()
          .pipe(
            map(doc => doc.exists && !doc.data().FECHA_BAJA_CS),
            switchMap(exists => {
              if (exists) {
                return of(batch.set(firestoreDoc.ref, {
                  FECHA_BAJA_CS: new Date()
                }, {
                  merge: true
                }))
              } else {
                return of(null)
              }
            })
          ).toPromise()
      });
    }).flat()
    return Promise.all(operations).then(() => batch);
  }

  private async getBase64ForDigitalSignature(digitalSignature: string) {

    return new Promise((resolve, reject) => {

      const fontSize = 16;
      const c = document.createElement('canvas');
      c.height = 50;
      c.width = digitalSignature.length * fontSize + 32

      let ctx = c.getContext('2d');
      ctx.font = fontSize + "px  Arial";
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, c.width, c.height);

      ctx.fillStyle = 'black';
      //ctx.strokeStyle = 'black';
      var x = c.width / 2;
      var y = c.height / 2;
      ctx.textAlign = 'center';
      ctx.fillText(digitalSignature, x, y);

      const data = ctx.canvas.toDataURL("image/jpeg").replace('data:image/jpeg;base64,', '');

      // console.log(data);

      resolve(data);

    });

  }
  private normalizeEmail(email: string): string {
    return email ? email.toLowerCase().trim() : null;
  }
  
  private async saveConsumer(
    formData: any,
    consumerTempId: any,
    DIAFN: string | null = null,
    MESFN: string | null = null,
    ANOFN: string | null = null,
    activeConsumerStand: number[],
    batch: firestore.WriteBatch,
    currentUserId?: string,
    isANewConsumer: boolean = true
  ): Promise<firestore.WriteBatch> {
    const relevantConsumerData = _.pick(formData, [
      'NOMBRE',
      'APELLIDO1',
      'APELLIDO2',
      'MOVIL',
      'TELEFONO',
      'EMAIL',
      'DNI_CONSUMIDORA',
      'COD_NACIONALIDAD',
      'TIPOVIA',
      'NOMBREVIA',
      'NUMEROVIA',
      'PISOPUERTA',
      'URBANIZ',
      'POSTAL',
      'PROVINCIA',
      'LOCALIDAD',
      'SEXO'
    ]);    const docContent = {
      EstimacionEdad: formData.RANGO_DE_EDAD,
      DIAFN,
      MESFN,
      ANOFN,
      DUPLICADO_CS: null,
      NORMALIZED_EMAIL: formData.EMAIL ? this.normalizeEmail(formData.EMAIL) : null,
      MayorDeEdad: formData.MAYOR_DE_EDAD,
      // Only set FECHA_ALTA_C for new consumers to preserve historical data
      ...(isANewConsumer && { FECHA_ALTA_C: new Date() }),
      FECHA_BAJA_C: null,
      ID_USUARIO: parseInt(currentUserId, 10) ?? null,
      FIRMA: formData.FIRMA.signature != null ? formData.FIRMA.signature : await this.getBase64ForDigitalSignature(formData.FIRMA),
      // Always update modification date
      FECHA_MODIFICACION_CONSUMIDORA: new Date(),
      ID_CONSUMIDORA: consumerTempId,
      ID_CONSUMIDORA_TMP: consumerTempId,
      array_CONSUMIDORA_MARCA: _.isNil(formData.BRANDS)
        ? []
        : this.selectedBrands(formData.BRANDS),
      array_CONSUMIDORA_STAND: activeConsumerStand.map(item => item.toString()),
      NombreBusqueda: `${this.replaceAccents(
        formData.NOMBRE
      )} ${this.replaceAccents(formData.APELLIDO1)} ${this.replaceAccents(
        formData.APELLIDO2
      )}`.trim(),
      ApellidosBusqueda: `${this.replaceAccents(formData.APELLIDO1)} ${this.replaceAccents(
        formData.APELLIDO2)} ${this.replaceAccents(formData.NOMBRE)}`.trim(),
      REVISAR_LOPD: false,
      ...relevantConsumerData
    };
    const consumerDocRef = this.afFirestore.doc<Partial<{ ID_USUARIO: number }>>(
      `${this.collectionName}/${consumerTempId}`
    );

    const promise = consumerDocRef.get().pipe(
      take(1),
      switchMap((docSnapshot) => {
        const data = docSnapshot.data();
        if (docSnapshot.exists && data && data.ID_USUARIO) {
          delete docContent.ID_USUARIO;
        }
        return from(consumerDocRef.set(docContent, { merge: true }));
      })
    ).toPromise();

    return promise.then(() => batch);

  }

  private saveHistoricConsumerBrand(
    currentUser: any,
    consumerTempId: number,
    formData: any,
    DIAFN: string | null = null,
    MESFN: string | null = null,
    ANOFN: string | null = null,
    brands: any[],
    batch: firestore.WriteBatch
  ) {
    const relevantHistoricConsumerData = {
      ID_Stand: '',
      ID_Marca: '',
      ID_USUARIO: currentUser.id,
      ID_CONSUMIDORA: consumerTempId,
      NOMBRE: formData.NOMBRE,
      APELLIDO1: formData.APELLIDO1,
      APELLIDO2: formData.APELLIDO2,
      TIPOVIA: formData.TIPOVIA,
      NOMBREVIA: formData.NOMBREVIA,
      NUMEROVIA: formData.NUMEROVIA,
      URBANIZ: formData.URBANIZ,
      POSTAL: formData.POSTAL,
      LOCALIDAD: formData.LOCALIDAD,
      PROVINCIA: formData.PROVINCIA,
      TELEFONO: formData.TELEFONO,
      MOVIL: formData.MOVIL,
      EMAIL: formData.EMAIL,
      DIAFN: DIAFN,
      MESFN: MESFN,
      ANOFN: ANOFN,
      DNI_CONSUMIDORA: formData.DNI_CONSUMIDORA
    };
    return this.saveEachHistoricConsumerBrand(
      brands,
      consumerTempId,
      relevantHistoricConsumerData,
      batch
    );
  }

  private saveConsumerBrandPreferences(
    consumerTempId: number,
    currentUser: any,
    formData: any,
    brands: string[],
    isANewConsumer: boolean,
    batch: firestore.WriteBatch,
    communicationsChannelsSettings: {
      [key: string]: {
        LOPD_CARTAS: boolean;
        LOPD_LLAMADAS: boolean;
        LOPD_EMAIL: boolean;
        LOPD_SMS: boolean;
      }
    },
    digitalSignatureId?: string
  ) {

    let relevantConsumerBrandData: Partial<ConsumerBrand> = {
      ID_CONSUMIDORA: consumerTempId,
      ID_USUARIO: currentUser.id,
      ID_MGM: formData.ID_MGM,
      FECHA_MGM: new Date(),
      FECHA_ALTA_CM: new Date(),
      FECHA_ACTUALIZACION_CM: new Date(),
      FECHA_ALTA_PF: null,
      FECHA_BAJA_CM: null,
      CUERPO: formData.CUERPO ?? null,
      SOLARES: formData.SOLARES ?? null,
      PROGRAMA_FIDELIDAD: formData.FIDELIDAD ? 1 : 0,
      PERMITE_LLAMADAS_MOVIL: formData.PERMITE_LLAMADAS_MOVIL,
      PERMITE_LLAMADAS_TELEFONO: formData.PERMITE_LLAMADAS_TELEFONO,
      ID_IPAD: digitalSignatureId ?? null
    };
    if (useDivision() == Divisions.GC) {
      relevantConsumerBrandData = {
        ...relevantConsumerBrandData,
        MAQUILLAJE: typeof formData.MAQUILLAJE === 'boolean' ? formData.MAQUILLAJE : null,
        OTROS: typeof formData.OTROS === 'boolean' ? formData.OTROS : null,
        FACIAL_BEAUTY: typeof formData.FACIAL_BEAUTY === 'boolean' ? formData.FACIAL_BEAUTY : null,
      }
    } else {
      relevantConsumerBrandData = {
        ...relevantConsumerBrandData,
        TRATAMIENTO: typeof formData.TRATAMIENTO === 'boolean' ? formData.TRATAMIENTO : null,
        MAQUILLAJE: typeof formData.MAQUILLAJE === 'boolean' ? formData.MAQUILLAJE : null,
        PERFUME: typeof formData.PERFUME === 'boolean' ? formData.PERFUME : null,
        HOMBRE: typeof formData.HOMBRE === 'boolean' ? formData.HOMBRE : null,
      }
    }

    if (!isANewConsumer) {
      delete relevantConsumerBrandData.FECHA_ALTA_CM;
      delete relevantConsumerBrandData.ID_USUARIO;
    }
    if (isANewConsumer) {
      relevantConsumerBrandData.FECHA_ACTUALIZACION_CM = null;
      relevantConsumerBrandData.IdTier = 0;
      relevantConsumerBrandData.DescripcionTier = "Tier0";
      relevantConsumerBrandData.UpdatedDate = new Date();
      relevantConsumerBrandData.COMPRA_MGM = false;
    }

    return this.saveEachConsumerBrand(
      isANewConsumer,
      brands,
      consumerTempId,
      relevantConsumerBrandData,
      batch,
      communicationsChannelsSettings
    );
  }

  replaceAccents(wordWithAccents: string): string {
    return wordWithAccents.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  }

  selectedBrands(brands: { [key: string]: boolean }[], getUnselected: boolean = false): string[] {
    const selectedBrands = [];
    _.forEach(brands, (value: boolean, key: string) => {

      if (getUnselected) {
        if (value === false && brands['ALL_BRANDS'] !== true) {
          selectedBrands.push(key);
        }
      } else {
        if (value === true || brands['ALL_BRANDS'] === true) {
          selectedBrands.push(key);
        }
      }
    });
    const index = selectedBrands.indexOf('ALL_BRANDS');
    if (index > -1) {
      selectedBrands.splice(index, 1);
    }
    return selectedBrands;
  }

  saveEachConsumerBrand(
    isANewConsumer: boolean,
    brands: string[],
    consumerId: string | number,
    relevantConsumerBrandData: Partial<ConsumerBrand>,
    batch: firestore.WriteBatch,
    communicationsChannelsSettings?: {
      [key: string]: {
        LOPD_CARTAS: boolean;
        LOPD_LLAMADAS: boolean;
        LOPD_EMAIL: boolean;
        LOPD_SMS: boolean;
      }
    }) {

    const originalIdMgm = relevantConsumerBrandData.ID_MGM;

    const creationOnEachBrand = brands.map(brandId => {
      const LOPDBrandSettings = this.getCommunicationChannelSettingsForABrand(communicationsChannelsSettings, brandId);

      if (originalIdMgm) {
        const splitId = relevantConsumerBrandData.ID_MGM.split('-');
        const idBrand = splitId[1];

        if (idBrand !== brandId) {
          relevantConsumerBrandData.ID_MGM = null;
          relevantConsumerBrandData.FECHA_MGM = null;
          relevantConsumerBrandData.COMPRA_MGM = null;
        } else {
          this.newMgmReferee(relevantConsumerBrandData.ID_MGM, Number(idBrand), Number(consumerId));
        }
      }

      if (isANewConsumer) {
        const relevantConsumerBrandDataWithLOPD: Partial<ConsumerBrand> = {
          ...LOPDBrandSettings,
          ...relevantConsumerBrandData
        };

        relevantConsumerBrandData.ID_MGM = originalIdMgm;
        this.newMgmConsumer(brandId, Number(consumerId));

        return this.newConsumerBrand(relevantConsumerBrandDataWithLOPD, brandId, consumerId, batch)
      } else {
        return new Promise((resolve, reject) => {
          this.getConsumerFechaAltaCM(parseInt(brandId, 10), consumerId as number)
            .subscribe((response: any) => {
              const relevantConsumerBrandDataWithLOPD: Partial<ConsumerBrand> = {
                ...LOPDBrandSettings,
                ...relevantConsumerBrandData,
                ...response
              }

              this.newMgmConsumer(brandId, Number(consumerId));
              relevantConsumerBrandData.ID_MGM = originalIdMgm;

              resolve(this.newConsumerBrand(relevantConsumerBrandDataWithLOPD, brandId, consumerId, batch));
            }, reject);
        });
      }

    });
    return Promise.all(creationOnEachBrand).then(() => batch);
  }

  deleteCustomerFromUncheckedBrands(brands: string[], consumerId: number, batch: firestore.WriteBatch) {
    const consumerIdAsIntOrString = consumerId;
    const brandsArray = [...brands.map(brand => parseInt(brand, 10))];

    if (brandsArray.length > 0) {
      let operations = [];
      brandsArray.forEach(brand => {
        const consumerDocRef = this.afFirestore.doc(`${this.collectionName}/${consumerId}`).ref;
        return batch.update(consumerDocRef, {
          array_CONSUMIDORA_MARCA: firestore.FieldValue.arrayRemove(brand.toString())
        });
      });
      if (brandsArray.length > firestoreMaxClauses) {
        const chunksOfBrands = _.chunk(brandsArray, firestoreMaxClauses);
        chunksOfBrands.forEach(chunk => {
          const query: QueryFn = (ref) => ref
            .where('ID_CONSUMIDORA', '==', consumerIdAsIntOrString)
            .where('ID_MARCA', 'in', chunk);
          return this.afFirestore.collection(this.consumerBrandCollectionName, query).get().pipe(
            tap(data => data.docs.forEach(doc => {
              return batch.set(doc.ref, {
                FECHA_BAJA_CM: new Date()
              }, { merge: true })
            })),
            take(1)
          ).subscribe();
        });
      } else {
        const query: QueryFn = (ref) => ref
          .where('ID_CONSUMIDORA', '==', consumerIdAsIntOrString)
          .where('ID_MARCA', 'in', brandsArray);
        this.afFirestore.collection(this.consumerBrandCollectionName, query).get().pipe(
          tap(data => data.docs.forEach(doc => {
            return batch.set(doc.ref, {
              FECHA_BAJA_CM: new Date()
            }, { merge: true })
          })
          ),
          take(1)
        ).subscribe();
      }

      return Promise.resolve(batch);
    }
    return Promise.resolve(batch);
  }

  saveEachHistoricConsumerBrand(brands: string[], consumerId: number, relevantHistoricConsumerData: {}, batch: firestore.WriteBatch) {
    const eachConsumerBrandCreation = brands
      .map(brandId => this.newHistoricConsumerBrand(relevantHistoricConsumerData, brandId, consumerId, batch));
    return Promise.all(eachConsumerBrandCreation).then(() => batch);
  }

  getAllBrandsWithConsumer(consumerId: number | string) {
    const query: QueryFn = (ref) => ref.where('ID_CONSUMIDORA', '==', consumerId);
    return this.afFirestore.collection<ConsumerBrand>(this.consumerBrandCollectionName, query).get();
  }

  newLogMail(consumerId: number, email: string, batch: firestore.WriteBatch) {
    const formattedDate = formatDate(new Date(), 'yyyyMMdd HH:mm:ss');
    const docName = `${consumerId}-${formattedDate}`;
    const ref = this.afFirestore.doc(`${this.logmailCollectionName}/${docName}`).ref
    return batch.set(ref, {
      EMAIL: email,
      FEC_CBO_MAIL: new Date(),
      ID_CONSUMIDORA: consumerId,
      TIPO: 1
    })
  }

  private newHistoricConsumerBrand(dataToSave: {}, brandId: string, consumerId: number, batch: firestore.WriteBatch) {
    const id = this.afFirestore.createId();
    const firestoreDoc = this.afFirestore.doc(`${this.consumerHistoricCollectionName}/${id}`)
    return Promise.resolve(batch.set(firestoreDoc.ref, { ID_MARCA: brandId, ...dataToSave }));
  }

  /**
   * updateConsumerBrandPreferences
   */
  public updateConsumerBrandPreferences(consumerId: string | number, brandId: string, preferences: Pick<ConsumerBrand, 'LOPD_CARTAS' | 'LOPD_EMAIL' | 'LOPD_LLAMADAS' | 'LOPD_SMS'>) {
    return this.afFirestore.doc<Pick<ConsumerBrand, 'LOPD_CARTAS' | 'LOPD_EMAIL' | 'LOPD_LLAMADAS' | 'LOPD_SMS' | 'ID_CONSUMIDORA'>>(`${this.consumerBrandCollectionName}/${consumerId}-${brandId}`)
      .set({
        ...preferences,
        ID_CONSUMIDORA: consumerId
      }, { merge: true });
  }

  public updateConsumerFidelityStatus(consumerId: Consumer['ID_CONSUMIDORA'], brandId: Brand['ID_MARCA'], newFidelityStatus: ConsumerBrand['PROGRAMA_FIDELIDAD']) {
    return this.afFirestore.doc<ConsumerBrand>(`${this.consumerBrandCollectionName}/${consumerId}-${brandId}`)
      .update({
        FECHA_ACTUALIZACION_CM: new Date(),
        FECHA_ALTA_PF: new Date(),
        PROGRAMA_FIDELIDAD: newFidelityStatus,
        // ID_CONSUMIDORA: consumerId,
        // ID_MARCA: brandId
      })
  }

  public getFidelityProgram(consumerId: Consumer['ID_CONSUMIDORA'], brandId: Brand['ID_MARCA']) {
    return this.afFirestore.doc<ConsumerBrand>(`${this.consumerBrandCollectionName}/${consumerId}-${brandId}`)
      .get().pipe(
        map((snapshot) => snapshot.data().PROGRAMA_FIDELIDAD)
      );
  }

  public getConsumerTierDesc(consumerId: Consumer['ID_CONSUMIDORA'], brandId: Brand['ID_MARCA']) {
    return this.afFirestore.doc<ConsumerBrand>(`${this.consumerBrandCollectionName}/${consumerId}-${brandId}`)
      .get().pipe(
        map((snapshot) => (snapshot.data()?.DescripcionTier) ?? '')
      );
  }

  private newConsumerBrand(dataToSave: Partial<ConsumerBrand>, brandId: string, consumerId: string | number, batch: firestore.WriteBatch) {
    const consumerBrandOperation = this.afFirestore.doc(`${this.consumerBrandCollectionName}/${consumerId}-${brandId}`);
    batch.set(consumerBrandOperation.ref, { ID_MARCA: parseInt(brandId, 10), ...dataToSave }, { merge: true });
    return Promise.resolve(batch);
  }

  public newConsumerStand(dataToSave: ConsumerStand) {
    return this.afFirestore.collection<ConsumerStand>(`${this.consumerStandCollectionName}`).add(dataToSave);
  }

  private searchByField(textSearch: string): string[] {
    // Check filters
    return textSearch
      // Check if is email
      ? /@/.test(textSearch)
        ? ['EMAIL']
        // Check if includes number
        : /\d/.test(textSearch)
          ? ['MOVIL', 'TELEFONO', 'ID_CONSUMIDORA']
          // Is string
          : ['NombreBusqueda', 'ApellidosBusqueda']
      // No filters
      : [''];
  }

  private getSearchQuery<T>(
    ref: Query<T>,
    field: string,
    options: {
      filters?: { textSearch: string, searchIn: ConsumerSearchCentersFilter };
      offset?: number;
      limit?: number;
    },
    { stand, brands }: { stand: Stand, brands?: Brand[] },
    brandIndex: number
  ): Query<T> {
    let query = ref
      .where('FECHA_BAJA_C', '==', null)
      .where('DUPLICADO_CS', '==', null);

    if (field) {
      const isNumber = ['MOVIL', 'TELEFONO', 'ID_CONSUMIDORA'].includes(field);
      const formatField = isNumber || field === 'EMAIL'
        ? options.filters.textSearch
        : options.filters.textSearch.toUpperCase();

      if (isNumber) {
        if ('ID_CONSUMIDORA' === field) {
          const numericValue = +formatField;
          query = query.where(field, '==', numericValue);
        } else {
          query = query.where(field, '==', formatField);
        }
      } else {
        query = query
          .orderBy(field)
          .startAt(formatField)
          .endAt(formatField + '\uf8ff');
      }
    }

    if (options.filters?.searchIn === ConsumerSearchCentersFilter.STAND) {
      query = query.where(
        'array_CONSUMIDORA_STAND',
        'array-contains',
        stand.ID_STAND.toString()
      );
    } else if (brands && options.filters?.searchIn === ConsumerSearchCentersFilter.ALL) {
      query = query.where(
        'array_CONSUMIDORA_MARCA',
        'array-contains-any',
        brands
          .map(brand => brand.ID_MARCA.toString())
          .slice(
            brandIndex * firestoreMaxClauses,
            brandIndex * firestoreMaxClauses + firestoreMaxClauses
          )
      );
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  }

  public userParams$(role: Roles, searchInStand: boolean): Observable<{ stand: Stand, brands?: Brand[] }> {
    this.userPermissions = this.permissionService.getPermissionRoles();
    if (
      !(this.userPermissions?.Consumidoras?.validationFunction as Array<string>)?.includes(`${Permission.Consumidoras}${PermAction.Datos}${PermActionOption.all}`)
    ) {
      return throwError('Users with this role can\'t search consumers.');
    }

    // if (searchInStand) {
    //   return this.userService.getDefaultStand().pipe(
    //     take(1),
    //     map(stand => ({ stand }))
    //   );
    // }

    return this.userService.getDefaultStand().pipe(
      take(1),
      switchMap(stand =>
        this.brandService.getBrandsByStand(stand).pipe(
          take(1),
          map(brands => ({ stand, brands }))
        )
      )
    );
  }

  public async generateCustomerDocId(): Promise<string> {
    const idConsumidora = this.afFirestore.createId();
    return idConsumidora;
  }

  public getConsumerSignature(consumerId: string): Observable<string> {
    return this.afStorage.ref(`firmas/${consumerId}.jpg`).getDownloadURL();
  }

  public changeIsSignatureSavedInCustomer(consumerId: string | number, isThereSignatureCaptured: boolean = false): Promise<void> {
    return this.afFirestore.doc<Partial<ConsumerSignature>>(`${this.consumerSignatureCollectionName}/${consumerId}`)
      .set({ HayFirmaCapturada: isThereSignatureCaptured }, { merge: true });
  }

  /**
   * @description Given a temporary consumer ID and a flag if the app is online or offline.
   * If the app is online it waits 3000ms and then get the ID_CONSUMIDORA (number). If not or error,
   * it returns the temporary consumer id.
   *
   * @param {string} tmpConsumerId
   * @param {boolean} isOnline
   * @return {*}  {Promise<string>}
   * @memberof ConsumerService
   */
  public getTmpConsumidoraIdDoc(tmpConsumerId: string, isOnline: boolean): Promise<string> {
    return of(isOnline).pipe(
      switchMap(isOnline => isOnline ? timer(3000) : timer(0)),
      switchMap(() => {
        return this.afFirestore.collection<TmpConsumerId>(this.tmpConsumerIdCollectionName, (ref) => ref.where('ID_ALFANUMERICO', '==', tmpConsumerId))
          .get()
      }),
      map((snapshot) => snapshot.docs[0].exists ? snapshot.docs[0].data() : null),
      map((doc: null | TmpConsumerId) => doc === null ? tmpConsumerId : doc.ID_CONSUMIDORA.toString()),
      catchError((err, caught) => {
        return of(tmpConsumerId);
      }),
    ).toPromise();
  }

  public async searchMgmCode(code: string): Promise<ConsumerGetMember> {
    const snapshot = await this.afFirestore.doc<ConsumerGetMember>(`${this.consumerGetMemberCollectionName}/${code}`).get().toPromise();

    if (snapshot.exists) {
      return snapshot.data()
    } else {
      return undefined;
    }
  }

  private async newMgmReferee(idMgm, idBrand, idConsumer): Promise<void> {
    let dataMgm;

    await this.afFirestore.collection(this.consumerGetMemberCollectionName).doc(idMgm)
      .collection(this.consumerGetMemberSubcollectionName).doc(String(idConsumer))
      .set({
        ID_CONSUMIDORA: idConsumer,
        ID_MARCA: idBrand,
        EMAIL_ENVIADO: false,
        created: new Date()
      });

    const snapshotMgm = await this.afFirestore.collection(this.consumerGetMemberCollectionName).doc(idMgm).get().toPromise();

    if (snapshotMgm.exists) {
      dataMgm = snapshotMgm.data()
    } else {
      dataMgm = undefined;
    }

    if (dataMgm !== undefined) {
      await this.afFirestore.collection(this.consumerGetMemberCollectionName).doc(idMgm).set({
        ...dataMgm,
        USOS_RESTANTES: dataMgm.USOS_RESTANTES - 1
      });
    }
  }
  public async newMgmConsumer(idBrand, idConsumer): Promise<void> {
    const numberIdBrand = Number(idBrand);

    //No he conseguido hacer funcionar this.brandService.getBrandById(idBrand)
    const snapshotBrand = await this.afFirestore.collection('MARCAS').doc(String(idBrand)).get().toPromise();

    if (snapshotBrand.exists) {
      const brandData = snapshotBrand.data();
      
      // Solo marcas que tengan DINERO_FIDELIDAD = 1 (fidelidad activada)
      if (brandData['DINERO_FIDELIDAD'] === 1) {
        let usos = 10;

        if (brandData['USOS_GET_MEMBER'] !== undefined) {
          usos = brandData['USOS_GET_MEMBER'];
        }
        
        const idMgm = `${idConsumer}-${idBrand}`;

        await this.afFirestore.collection(this.consumerGetMemberCollectionName).doc(idMgm)
          .set({
            ID_CONSUMIDORA: idConsumer,
            ID_MARCA: Number(idBrand),
            USOS_RESTANTES: usos,
            created_date: new Date()
          });
      }
    }
  }

  public async getMgmCodeToDisplay(consumer, userId): Promise<string> {
    let mgmCode;
    await this.userService.getUserBrandStandByDefault(userId).subscribe(res => {
      mgmCode = `${consumer.ID_CONSUMIDORA}-${res[0].ID_MARCA}`;
    });

    return mgmCode;
  }

  public getConsumerBrand(idConsumer, idBrand): Observable<any> {
    const query: QueryFn = (ref) =>
      ref.where('ID_CONSUMIDORA', '==', idConsumer)
        .where('ID_MARCA', '==', idBrand)
        .limit(1);

    return this.afFirestore
      .collection(this.consumerBrandCollectionName, query)
      .get()
      .pipe(
        map((data) => {
          const consumerBrand = data.docs.map((d) => d.data());
          return consumerBrand[0];
        })
      );
  }

  /**
   * Given the communicationsChannels for a given consumer this method takes the LOPD parameters for a specific brand.
   * @param communicationsChannelsSettings Object containing the Communications Channel Settings for all the brands the consumer is.
   * @param needleBrand The brandId in string format which we want to search on the communicationChannelsSettings
   */
  private getCommunicationChannelSettingsForABrand(
    communicationsChannelsSettings: { [key: string]: Pick<ConsumerBrand, 'LOPD_CARTAS' | 'LOPD_SMS' | 'LOPD_EMAIL' | 'LOPD_LLAMADAS'> },
    needleBrand: string
  ): Pick<ConsumerBrand, 'LOPD_CARTAS' | 'LOPD_SMS' | 'LOPD_EMAIL' | 'LOPD_LLAMADAS'> {
    if (!communicationsChannelsSettings || !needleBrand) {
      return;
    }
    const formattedCCSettings = Object.entries(communicationsChannelsSettings).map(entry => {
      entry[0] = entry[0].split('-')[1];
      return entry;
    });
    const filterByBrand = formattedCCSettings.filter(setting => setting[0] === needleBrand)
    return filterByBrand.length >= 1 ? filterByBrand[0][1] : { LOPD_CARTAS: false, LOPD_EMAIL: false, LOPD_LLAMADAS: false, LOPD_SMS: false };
  }
}
